import { useState } from "react"
import { useParams } from "react-router-dom"
import { caseStudies } from "~/content/cases"
import { services } from "~/content/services"
import { solutions } from "~/content/solutions"
import { workIndexCaseStudyIds, workIndexSummaries } from "~/content/work-index"
import { CaseStudyCard } from "~/marketing/case-study-card"
import { CaseStudyTemplate } from "~/marketing/case-study-template"
import { SourceContactEndcap } from "~/marketing/public-page"
import { WorkCover } from "~/marketing/work-cover"
import { NotFoundScreen } from "~/root/not-found-screen"

const serviceOptions = [
  ["ai-strategy", "AI Strategy"],
  ["web-development", "Development"],
  ["sprint-zero", "Sprint Zero"],
  ["design", "Design"],
  ["product-management", "Product"],
] as const
const businessOptions = [
  ["small-medium-businesses", "Small & Medium Businesses"],
  ["growth-startups", "Growth Startups"],
  ["early-startups", "Early-stage Startups"],
  ["private-equity", "Private Equity"],
  ["accelerators", "Accelerators"],
] as const

export const WorkIndexPage = () => {
  const [service, setService] = useState("")
  const [business, setBusiness] = useState("")
  // Reuse the curated project associations on the existing service and solution pages.
  const serviceProjects: readonly string[] | undefined = service
    ? services[service as keyof typeof services].relatedCaseStudyIds
    : undefined
  const businessProjects: readonly string[] | undefined = business
    ? solutions[business as (typeof businessOptions)[number][0]].relatedCaseStudyIds
    : undefined
  const projects = workIndexCaseStudyIds.filter(
    (id) =>
      (!serviceProjects || serviceProjects.includes(id)) &&
      (!businessProjects || businessProjects.includes(id))
  )
  const clearFilters = () => {
    setService("")
    setBusiness("")
  }
  return (
    <div className="work-page">
      <section className="container work-index-hero">
        <p className="work-index-eyebrow">Case Studies</p>
        <h1 className="work-index-title">
          <span className="headline-beat">See what we</span>{" "}
          <span className="home-emphasis home-highlight">built together.</span>
        </h1>
        <p className="work-index-intro">
          A first product. A growing platform. Better tools for an established business.
          <br className="hidden lg:block" /> Find a project that looks like what you have in mind.
        </p>
        <div className="work-filters">
          <div className="work-filter-fields">
            <label htmlFor="work-service">
              Service
              <select
                id="work-service"
                value={service}
                onChange={(event) => setService(event.target.value)}
                aria-controls="work-results"
              >
                <option value="">All services</option>
                {serviceOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="work-business">
              Business type
              <select
                id="work-business"
                value={business}
                onChange={(event) => setBusiness(event.target.value)}
                aria-controls="work-results"
              >
                <option value="">All business types</option>
                {businessOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="work-filter-status">
            <p role="status">
              {projects.length} {projects.length === 1 ? "project" : "projects"}
            </p>
            {(service || business) && (
              <button type="button" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>
      </section>
      <section id="work-results" aria-label="Projects" className="container work-index-grid">
        {projects.map((id, index) => (
          <CaseStudyCard
            key={id}
            id={id}
            priority={index === 0}
            summary={workIndexSummaries[id]}
            tags={[]}
            cover={<WorkCover id={id} />}
          />
        ))}
        {projects.length === 0 && (
          <div className="work-empty">
            <h2>No projects match both filters.</h2>
            <p>Try another combination or clear the filters.</p>
            <button type="button" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}
      </section>
      <SourceContactEndcap workPage />
    </div>
  )
}

export const CaseStudyPage = () => {
  const { slug } = useParams()
  const caseStudy = slug ? caseStudies[slug as keyof typeof caseStudies] : undefined

  if (!caseStudy) return <NotFoundScreen />

  return <CaseStudyTemplate caseStudy={caseStudy} />
}
